import React, { useCallback, useMemo, ReactNode } from 'react'
import {
  AnalysisModeProvider,
  AnalysisServicesProvider,
  type AnalysisModeContextValue,
  type AnalysisServicesContextValue,
  type EntityType,
} from '@popety-io/ui'
import { publicZoningService } from '../services/publicZoningService'

interface PublicAnalysisProviderProps {
  children: ReactNode
  entity: any
  entityType: EntityType
  userId: string
}

export const PublicAnalysisProvider: React.FC<PublicAnalysisProviderProps> = ({
  children,
  entity,
  entityType,
  userId,
}) => {
  const modeValue: AnalysisModeContextValue = useMemo(
    () => ({
      readOnly: true,
      entity,
      userId,
      entityType,
      prefetchedData: entity,
    }),
    [entity, userId, entityType]
  )

  const getSectionData = useCallback(
    (key: string) => {
      if (key === 'zoningService') return publicZoningService
      return entity?.[key] || null
    },
    [entity]
  )

  const updateSectionData = useCallback(
    async (_key: string, _entity: any, _userId: string, _params?: any) => {
      // No-op in public app - return existing data
      return entity?.[_key] || null
    },
    [entity]
  )

  const onDataFetched = useCallback((_key: string, _data: any) => {
    // No-op in public app - data is pre-fetched
  }, [])

  const servicesValue: AnalysisServicesContextValue = useMemo(
    () => ({
      getSectionData,
      updateSectionData,
      onDataFetched,
    }),
    [getSectionData, updateSectionData, onDataFetched]
  )

  return (
    <AnalysisModeProvider value={modeValue}>
      <AnalysisServicesProvider value={servicesValue}>
        {children}
      </AnalysisServicesProvider>
    </AnalysisModeProvider>
  )
}
